import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

const ACTION_COLOR = {
  BUY_NOW:     'var(--red)',
  BUY_TRANCHE: 'var(--amber)',
  HOLD:        'var(--green)',
  WAIT:        '#3b82f6',
};

const fmt = n => (n != null ? Math.round(n).toLocaleString() : '—');
const fmtPct = n => (n != null ? (n >= 0 ? '+' : '') + Number(n).toFixed(1) + '%' : '—');

function computeStats(rows) {
  const resolved = rows.filter(r => r.actual != null);
  if (!resolved.length) return { mape: null, hitRate: null, bias: null, resolvedCount: 0 };
  const hitRate  = Math.round(resolved.filter(r => r.hit).length / resolved.length * 100);
  const mape     = resolved.reduce((s, r) => s + Math.abs(r.error_pct), 0) / resolved.length;
  const bias     = resolved.reduce((s, r) => s + r.error_pct, 0) / resolved.length;
  return { mape: mape.toFixed(1), hitRate, bias: bias.toFixed(1), resolvedCount: resolved.length };
}

function joinPredictionsActuals(predictions, actuals) {
  const actualsMap = {};
  actuals.forEach(a => { actualsMap[a.observed_date] = a; });

  return predictions.map(p => {
    const targetDate = addDays(p.run_date, 7);
    const actual = actualsMap[targetDate] || null;
    const actualPrice = actual?.dce_m1_close ?? null;
    const hit = actualPrice != null ? (actualPrice >= p.p10_7d && actualPrice <= p.p90_7d) : null;
    const error_pct = (actualPrice != null && p.ev_7d)
      ? ((actualPrice - p.ev_7d) / p.ev_7d * 100)
      : null;
    return { ...p, actual: actualPrice, hit, error_pct };
  });
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

function cutoffDate(filter) {
  if (filter === '7d')  return addDays(new Date().toISOString().split('T')[0], -7);
  if (filter === '30d') return addDays(new Date().toISOString().split('T')[0], -30);
  return null;
}

export default function Performance({ addToast }) {
  const { t }      = useTranslation();
  const { isAdmin, session } = useAuth();

  const [predictions, setPredictions] = useState([]);
  const [actuals, setActuals]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [timeFilter, setTimeFilter]   = useState('30d');
  const [srcFilter, setSrcFilter]     = useState('all');
  const [triggering, setTriggering]   = useState({ snapshot: false, actuals: false });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [predRes, actRes] = await Promise.all([
        supabase.from('predictions').select('*').order('created_at', { ascending: false }).limit(200),
        supabase.from('actuals').select('*').order('observed_date', { ascending: false }).limit(400),
      ]);
      setPredictions(predRes.data || []);
      setActuals(actRes.data || []);
    } catch {
      // RLS: non-admins get empty arrays — handled below
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="page-hdr">
          <h2>Access denied</h2>
          <p>Only admins can view VERITAS.</p>
        </div>
      </div>
    );
  }

  // Filter predictions
  const cutoff = cutoffDate(timeFilter);
  let filtered = joinPredictionsActuals(predictions, actuals);
  if (cutoff) filtered = filtered.filter(r => r.run_date >= cutoff);
  if (srcFilter !== 'all') filtered = filtered.filter(r => r.source === srcFilter);

  // Stats over 30d resolved for summary strip (always 30d regardless of table filter)
  const rows30d  = joinPredictionsActuals(predictions, actuals)
    .filter(r => r.run_date >= cutoffDate('30d'));
  const stats    = computeStats(rows30d);
  const hasStats = stats.resolvedCount >= 7;

  // Bias label
  const biasVal  = stats.bias != null ? parseFloat(stats.bias) : null;
  const biasDir  = biasVal == null ? null : biasVal > 0.5 ? 'under' : biasVal < -0.5 ? 'over' : 'neutral';
  const biasDirLabel = biasDir ? t(`veritas.bias_${biasDir}`) : '—';

  async function triggerJob(job) {
    setTriggering(s => ({ ...s, [job]: true }));
    try {
      const res = await fetch('/api/admin/trigger-cron', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ job }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        addToast(`${t('veritas.trigger_fail')}: ${data.error || res.status}`, 'error');
      } else if (data.skipped) {
        addToast(`${t('veritas.trigger_success')} (${data.reason})`, 'default');
      } else {
        addToast(t('veritas.trigger_success'), 'success');
      }
      await loadData();
    } catch (e) {
      addToast(`${t('veritas.trigger_fail')}: ${e.message}`, 'error');
    }
    setTriggering(s => ({ ...s, [job]: false }));
  }

  return (
    <>
      {/* Header */}
      <div className="page-hdr" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>📐</div>
        <h2 style={{ letterSpacing: '.06em' }}>{t('veritas.title')}</h2>
        <p style={{ fontSize: 13, color: 'var(--t2)', marginTop: 4 }}>{t('veritas.subtitle')}</p>
      </div>

      {/* Summary strip */}
      <div className="card-hdr" style={{ marginBottom: 8 }}>{t('veritas.section_summary')}</div>
      <div className="veritas-stat-grid">
        {/* Forecast band capture */}
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--t3)', letterSpacing: '.08em', marginBottom: 6 }}>
            {t('veritas.stat_band_capture').toUpperCase()}
          </div>
          {hasStats
            ? <div className="veritas-stat-value" style={{ color: stats.hitRate >= 60 ? 'var(--green)' : stats.hitRate >= 40 ? 'var(--amber)' : 'var(--red)' }}>
                {stats.hitRate}%
              </div>
            : <div className="veritas-stat-value" style={{ color: 'var(--t3)', fontSize: 22 }}>—</div>
          }
          <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4 }}>30d · {stats.resolvedCount} resolved</div>
        </div>

        {/* MAPE */}
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--t3)', letterSpacing: '.08em', marginBottom: 6 }}>
            {t('veritas.stat_mape').toUpperCase()}
          </div>
          {hasStats
            ? <div className="veritas-stat-value" style={{ color: parseFloat(stats.mape) <= 3 ? 'var(--green)' : parseFloat(stats.mape) <= 8 ? 'var(--amber)' : 'var(--red)' }}>
                {stats.mape}%
              </div>
            : <div className="veritas-stat-value" style={{ color: 'var(--t3)', fontSize: 22 }}>—</div>
          }
          <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4 }}>30d · mean abs error</div>
        </div>

        {/* Bias */}
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--t3)', letterSpacing: '.08em', marginBottom: 6 }}>
            {t('veritas.stat_bias').toUpperCase()}
          </div>
          {hasStats
            ? <div className="veritas-stat-value" style={{ color: biasDir === 'neutral' ? 'var(--green)' : 'var(--amber)' }}>
                {fmtPct(stats.bias)}
              </div>
            : <div className="veritas-stat-value" style={{ color: 'var(--t3)', fontSize: 22 }}>—</div>
          }
          <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4 }}>{biasDirLabel}</div>
        </div>
      </div>

      {!hasStats && (
        <div className="card" style={{ textAlign: 'center', padding: '20px 24px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>
            {t('veritas.insufficient_data_title')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t3)', maxWidth: 400, margin: '0 auto' }}>
            {t('veritas.insufficient_data_body')}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0 8px' }}>
        {['7d', '30d', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setTimeFilter(f)}
            className="btn"
            style={{
              fontSize: 12, padding: '6px 14px', minHeight: 32,
              background: timeFilter === f ? 'var(--accent)' : 'rgba(42,48,68,.5)',
              color: timeFilter === f ? '#fff' : 'var(--t2)',
            }}
          >
            {t(`veritas.filter_${f}`)}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {['all', 'cron_daily', 'manual_save'].map(s => (
          <button
            key={s}
            onClick={() => setSrcFilter(s)}
            className="btn"
            style={{
              fontSize: 12, padding: '6px 14px', minHeight: 32,
              background: srcFilter === s ? 'rgba(59,130,246,.2)' : 'rgba(42,48,68,.5)',
              color: srcFilter === s ? '#3b82f6' : 'var(--t2)',
            }}
          >
            {s === 'all' ? t('veritas.filter_all_sources') : s === 'cron_daily' ? t('veritas.filter_cron') : t('veritas.filter_manual')}
          </button>
        ))}
      </div>

      {/* Prediction history */}
      <div className="card-hdr" style={{ marginBottom: 8 }}>{t('veritas.section_history')}</div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--t3)', padding: 32, fontSize: 13 }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--t3)', padding: 24, fontSize: 13 }}>
          {predictions.length === 0 ? t('veritas.no_predictions') : 'No records match the current filter.'}
        </div>
      ) : (
        <div className="veritas-table-wrap">
          <table className="veritas-table">
            <thead>
              <tr>
                <th>{t('veritas.col_date')}</th>
                <th>{t('veritas.col_action')}</th>
                <th className="veritas-num">{t('veritas.col_ev')}</th>
                <th className="veritas-num">{t('veritas.col_p10')}</th>
                <th className="veritas-num">{t('veritas.col_p90')}</th>
                <th className="veritas-num">{t('veritas.col_actual')}</th>
                <th className="veritas-num">{t('veritas.col_error')}</th>
                <th style={{ textAlign: 'center' }}>{t('veritas.col_hit')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(107,115,148,.1)' }}>
                  <td>
                    <div style={{ fontSize: 12 }}>{r.run_date}</div>
                    <div style={{ fontSize: 10, color: 'var(--t3)' }}>
                      {r.source === 'cron_daily' ? 'cron' : 'manual'}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, color: ACTION_COLOR[r.action] || 'var(--t2)' }}>
                      {r.action?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="veritas-num">¥{fmt(r.ev_7d)}</td>
                  <td className="veritas-num" style={{ color: 'var(--red)', fontSize: 11 }}>¥{fmt(r.p10_7d)}</td>
                  <td className="veritas-num" style={{ color: 'var(--green)', fontSize: 11 }}>¥{fmt(r.p90_7d)}</td>
                  <td className="veritas-num">
                    {r.actual != null
                      ? <span style={{ fontWeight: 600 }}>¥{fmt(r.actual)}</span>
                      : <span style={{ color: 'var(--t3)', fontSize: 11 }}>{t('veritas.pending')}</span>
                    }
                  </td>
                  <td className="veritas-num">
                    {r.error_pct != null
                      ? <span style={{ color: r.error_pct >= 0 ? 'var(--green)' : 'var(--red)', fontSize: 12, fontWeight: 600 }}>
                          {fmtPct(r.error_pct)}
                        </span>
                      : <span style={{ color: 'var(--t3)' }}>—</span>
                    }
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {r.hit === true  && <span style={{ color: 'var(--green)', fontWeight: 700 }}>{t('veritas.hit_yes')}</span>}
                    {r.hit === false && <span style={{ color: 'var(--red)',   fontWeight: 700 }}>{t('veritas.hit_no')}</span>}
                    {r.hit === null  && <span style={{ color: 'var(--t3)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footnote */}
      <div style={{ fontSize: 10, color: 'var(--t3)', fontStyle: 'italic', marginTop: 12, lineHeight: 1.5 }}>
        {t('veritas.footnote')}
      </div>

      {/* Maintenance */}
      <div className="card-hdr" style={{ marginTop: 28, marginBottom: 8 }}>{t('veritas.section_maintenance')}</div>
      <div className="card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          className="btn"
          disabled={triggering.snapshot}
          onClick={() => triggerJob('snapshot')}
          style={{ fontSize: 12 }}
        >
          {triggering.snapshot ? t('veritas.trigger_running') : t('veritas.trigger_snapshot')}
        </button>
        <button
          className="btn"
          disabled={triggering.actuals}
          onClick={() => triggerJob('actuals')}
          style={{ fontSize: 12 }}
        >
          {triggering.actuals ? t('veritas.trigger_running') : t('veritas.trigger_actuals')}
        </button>
      </div>
    </>
  );
}
